import type { QualityMode, VisualSettings } from '../config/appConfig';
import type { ViewMode } from '../cesium/firstPersonController';

export interface ControlPanelCallbacks {
  onQualityChange: (quality: QualityMode) => void;
  onTimeChange: (date: Date) => void;
  onVisualSettingsChange: (settings: VisualSettings) => void;
  onViewModeChange: (mode: ViewMode) => void;
}

function toLocalDateTimeValue(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  return [
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  ].join('T');
}

export class ControlPanel {
  private readonly quality = document.getElementById(
    'qualitySelect',
  ) as HTMLSelectElement | null;
  private readonly time = document.getElementById(
    'timeControl',
  ) as HTMLInputElement | null;
  private readonly nowButton = document.getElementById(
    'timeNowButton',
  ) as HTMLButtonElement | null;
  private readonly shadows = document.getElementById(
    'shadowToggle',
  ) as HTMLInputElement | null;
  private readonly atmosphere = document.getElementById(
    'atmosphereToggle',
  ) as HTMLInputElement | null;
  private readonly fog = document.getElementById(
    'fogToggle',
  ) as HTMLInputElement | null;
  private readonly fogDensity = document.getElementById(
    'fogDensityControl',
  ) as HTMLInputElement | null;
  private readonly fogDensityValue = document.getElementById(
    'fogDensityValue',
  ) as HTMLOutputElement | null;
  private readonly hdr = document.getElementById(
    'hdrToggle',
  ) as HTMLInputElement | null;
  private readonly nightLighting = document.getElementById(
    'nightLightingToggle',
  ) as HTMLInputElement | null;
  private readonly viewModeButton = document.getElementById(
    'viewModeButton',
  ) as HTMLButtonElement | null;
  private viewMode: ViewMode = 'overview';

  constructor(callbacks: ControlPanelCallbacks) {
    this.quality?.addEventListener('change', () => {
      const value = this.quality?.value;
      if (value === 'eco' || value === 'standard' || value === 'high') {
        callbacks.onQualityChange(value);
      }
    });

    this.time?.addEventListener('change', () => {
      if (!this.time?.value) return;
      const date = new Date(this.time.value);
      if (!Number.isNaN(date.getTime())) callbacks.onTimeChange(date);
    });

    this.nowButton?.addEventListener('click', () => {
      const now = new Date();
      if (this.time) this.time.value = toLocalDateTimeValue(now);
      callbacks.onTimeChange(now);
    });

    const onVisualChange = (): void => {
      const settings = this.readVisualSettings();
      if (this.fogDensityValue) {
        this.fogDensityValue.value = settings.fogDensity.toFixed(5);
      }
      callbacks.onVisualSettingsChange(settings);
    };
    this.shadows?.addEventListener('change', onVisualChange);
    this.atmosphere?.addEventListener('change', onVisualChange);
    this.fog?.addEventListener('change', onVisualChange);
    this.fogDensity?.addEventListener('input', onVisualChange);
    this.hdr?.addEventListener('change', onVisualChange);
    this.nightLighting?.addEventListener('change', onVisualChange);
    this.viewModeButton?.addEventListener('click', () => {
      const nextMode: ViewMode = this.viewMode === 'overview'
        ? 'first-person'
        : 'overview';
      callbacks.onViewModeChange(nextMode);
    });
  }

  setQuality(quality: QualityMode): void {
    if (this.quality) this.quality.value = quality;
  }

  setTime(date: Date): void {
    if (this.time) this.time.value = toLocalDateTimeValue(date);
  }

  setVisualSettings(settings: VisualSettings): void {
    if (this.shadows) this.shadows.checked = settings.shadows;
    if (this.atmosphere) this.atmosphere.checked = settings.atmosphere;
    if (this.fog) this.fog.checked = settings.fog;
    if (this.fogDensity) this.fogDensity.value = String(settings.fogDensity);
    if (this.fogDensityValue) {
      this.fogDensityValue.value = settings.fogDensity.toFixed(5);
    }
    if (this.hdr) this.hdr.checked = settings.hdr;
    if (this.nightLighting) this.nightLighting.checked = settings.nightLighting;
  }

  setViewMode(mode: ViewMode): void {
    this.viewMode = mode;
    if (this.viewModeButton) {
      this.viewModeButton.textContent = mode === 'first-person'
        ? '俯瞰視点に戻す'
        : '人視点で歩く';
      this.viewModeButton.setAttribute(
        'aria-pressed',
        String(mode === 'first-person'),
      );
    }
  }

  private readVisualSettings(): VisualSettings {
    return {
      shadows: this.shadows?.checked ?? false,
      atmosphere: this.atmosphere?.checked ?? true,
      fog: this.fog?.checked ?? true,
      fogDensity: Number(this.fogDensity?.value ?? 0.00035),
      hdr: this.hdr?.checked ?? false,
      nightLighting: this.nightLighting?.checked ?? true,
    };
  }
}
